import React from "react";
import retailImage from "../../assets/images/hero/retailPos.png"; // Use a retail-relevant image
import {
  retailScreens,
  retailFeatureCards,
  retailPricingPlans,
} from "../../data/homeContent";
import {
  QrCodeIcon,
  CreditCardIcon,
  Squares2X2Icon,
  DeviceTabletIcon,
  TicketIcon,
  PrinterIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  ArrowPathIcon,
  LockClosedIcon,
  CubeTransparentIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "../../context/CurrencyContext";

// Features Component

const Retail = () => {
  const { currency, symbol } = useCurrency();
  const [billing, setBilling] = React.useState("monthly"); // 'monthly' or 'annually'
  const featuresRef = React.useRef(null); // <-- Add this line
  const navigate = useNavigate();

  return (
    <>
      <div className="flex flex-col items-center justify-center p-4 md:p-12 bg-gradient-to-br from-blue-200 via-white to-blue-100">
        {" "}
        {/* Left Side: Text Content */}
        <div className="md:w-1/2 w-full flex flex-col items-center justify-center mb-8 md:mb-0 md:pr-12">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900 leading-tight drop-shadow-sm text-center">
            Elevate Your Retail Business with{" "}
            <span className="text-blue-600  px-2 py-1 rounded-lg">
              RetailPro
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-8 text-center">
            Streamline your retail operations, enhance customer experiences, and
            boost efficiency with our all-in-one retail management platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center w-full">
            <button
              onClick={() => {
                if (
                  window.Tawk_API &&
                  typeof window.Tawk_API.toggle === "function"
                ) {
                  window.Tawk_API.toggle();
                } else {
                  // Optionally, show a message or fallback
                  alert("Live chat is loading, please try again in a moment.");
                }
              }}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white px-7 py-3 rounded-xl shadow-lg hover:scale-105 hover:from-blue-700 hover:to-blue-500 transition-all duration-200 font-semibold justify-center"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M8 17l4 4 4-4m-4-5v9" />
              </svg>
              Book a Demo
            </button>
            <button
              className="flex items-center gap-2 bg-white border border-blue-200 text-blue-600 px-7 py-3 rounded-xl shadow hover:bg-blue-50 hover:scale-105 transition-all duration-200 font-semibold justify-center"
              onClick={() => {
                featuresRef.current?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M15 12H9m12 0A9 9 0 11 3 12a9 9 0 0118 0z" />
              </svg>
              See Features
            </button>
          </div>
        </div>
        {/* Right Side: Main Image */}
        <div className="md:w-1/2 w-full flex justify-center items-center mt-10">
          <div className="relative group flex justify-center items-center">
            <div className="absolute -inset-2 bg-blue-200 opacity-30 rounded-2xl blur-lg group-hover:scale-105 transition-transform duration-300"></div>
            <img
              src={retailImage}
              alt="RetailPro Dashboard"
              className="w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-300"
            />
          </div>
        </div>
      </div>
      <div className="p-8">
        {/* Screenshots Gallery */}
        <div className="mt-32">
          <h4 className="text-2xl font-bold mb-8 text-center text-blue-700 tracking-wide">
            Screens & Features
          </h4>
          <div className="flex flex-col gap-12">
            {retailScreens.map((screen, idx) => (
              <div
                className={`flex flex-col md:flex-row items-center bg-white rounded-3xl shadow-2xl border border-blue-100 p-8 gap-10 ${idx % 2 === 1 ? "md:flex-row-reverse" : ""
                  }`}
                key={idx}
              >
                {/* Image */}
                <div className="w-full md:w-1/2 flex justify-center">
                  <img
                    src={screen.src}
                    alt={screen.alt}
                    className="rounded-2xl object-contain shadow-xl border-4 border-white w-[320px] h-[320px] md:w-[420px] md:h-[420px]  "
                  />
                </div>
                {/* Content/Features */}
                <div className="w-full md:w-1/2 flex flex-col items-start md:items-start text-left">
                  <span className="text-blue-600 text-lg font-semibold mb-2 uppercase tracking-wider">
                    {screen.caption}
                  </span>
                  {screen.subtitle && (
                    <h5 className="text-2xl font-bold text-gray-900 mb-3">
                      {screen.subtitle}
                    </h5>
                  )}
                  {screen.features && (
                    <ul className="list-disc list-inside text-base text-gray-700 space-y-2 mb-2">
                      {screen.features.map((feature, fidx) => (
                        <li key={fidx} className="pl-1">
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Optional: Add a call-to-action or badge */}
                  {/* <span className="inline-block mt-4 px-4 py-1 bg-pink-50 text-pink-700 rounded-full text-xs font-semibold shadow">
                  Learn More
                </span> */}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Powerful Features Cards */}
        <div className="mt-32" ref={featuresRef}>
          <h3 className="text-3xl font-extrabold text-center text-gray-900 mb-8">
            Powerful Features That Work The Way You Do
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {retailFeatureCards.map((feature, idx) => {
              const Icon = {
                QrCodeIcon,
                CreditCardIcon,
                Squares2X2Icon,
                DeviceTabletIcon,
                TicketIcon,
                PrinterIcon,
                UserGroupIcon,
                BuildingStorefrontIcon,
                ChartBarIcon,
                ArrowPathIcon,
                LockClosedIcon,
                CubeTransparentIcon,
              }[feature.icon];
              return (
                <div
                  key={idx}
                  className="relative group bg-white/60 backdrop-blur-lg border-2 border-transparent bg-clip-padding rounded-3xl shadow-xl p-8 flex flex-col items-center transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl hover:border-blue-300"
                  style={{
                    borderImage:
                      "linear-gradient(135deg, #f472b6 0%, #60a5fa 100%) 1",
                  }}
                >
                  {/* Animated Gradient Circle Behind Icon */}
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-0">
                    <span className="inline-block w-20 h-20 rounded-full bg-gradient-to-tr from-blue-200 via-blue-100 to-blue-100 blur-lg opacity-70 group-hover:scale-110 transition-transform duration-300"></span>
                  </div>
                  {/* Icon */}
                  <div className="relative z-10 mb-4">
                    <div className="bg-white shadow-lg rounded-full p-4 border-2 border-blue-100 group-hover:border-blue-400 transition-colors duration-300">
                      {Icon && <Icon className="h-8 w-8 text-blue-600" />}
                    </div>
                  </div>
                  {/* Title & Description */}
                  <h4 className="text-lg font-extrabold text-gray-900 mb-2 text-center drop-shadow-sm">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600 text-center text-sm">
                    {feature.description}
                  </p>
                  {/* Floating badge */}
                  <span className="absolute top-4 right-4 bg-blue-100 text-blue-600 text-xs font-semibold px-3 py-1 rounded-full shadow group-hover:bg-blue-200 transition-all duration-300">
                    NEW
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        {/* Pricing Section */}
        <div className="mt-32">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-gray-900 mb-2">
            Simple, Transparent Pricing —{" "}
            <span className="text-blue-600">No Surprises</span>
          </h2>
          <p className="text-center text-lg text-gray-600 mb-8">
            Unlock every essential feature for one clear price.{" "}
            <span className="font-semibold text-blue-600">
              No hidden fees, no contracts, no hassle.
            </span>{" "}
            Choose the plan that fits your business and start growing today!
          </p>
          {/* Billing Toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-gray-100 rounded-full p-1">
              <button
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-200 ${billing === "monthly"
                  ? "bg-blue-600 text-white shadow"
                  : "text-gray-700 hover:bg-gray-200"
                  }`}
                onClick={() => setBilling("monthly")}
              >
                Pay Monthly
              </button>
              <button
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-200 ${billing === "annually"
                  ? "bg-blue-600 text-white shadow"
                  : "text-gray-700 hover:bg-gray-200"
                  }`}
                onClick={() => setBilling("annually")}
              >
                Pay Annually
              </button>
            </div>
          </div>
          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {retailPricingPlans.map((plan, idx) => (
              <div
                key={idx}
                className={`relative flex flex-col rounded-3xl shadow-2xl border-2 ${plan.highlight
                  ? "border-blue-500 bg-white/90 scale-105 z-10"
                  : "border-gray-100 bg-white/70"
                  } p-4 transition-all duration-300 hover:scale-105`}
              >
                {plan.highlight && (
                  <span className="absolute top-4 right-4 bg-blue-100 text-blue-600 text-xs font-bold px-3 py-1 rounded-full shadow">
                    Most Popular
                  </span>
                )}
                <h3 className="text-xl font-bold mb-2 text-gray-900">
                  {plan.name}
                </h3>
                <div className="flex items-end mb-4">
                  {plan.priceOneTime ? (
                    <>
                      <span className="text-3xl font-extrabold text-blue-600">
                        £{plan.priceOneTime.toLocaleString()}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">
                        Pay One Time
                      </span>
                    </>
                  ) : (
                    <>
                      {plan.priceMonthly !== null && (
                        <>
                          <span className="text-3xl font-extrabold text-blue-600">
                            {symbol}
                            {currency === "INR"
                              ? billing === "monthly"
                                ? plan.priceMonthlyINR
                                : Math.round(plan.priceAnnuallyINR)
                              : billing === "monthly"
                                ? plan.priceMonthly
                                : Math.round(plan.priceAnnually)}
                            .00
                          </span>

                          <span className="ml-2 text-sm text-gray-500">
                            {plan?.name !== "Life Time"
                              ? billing === "monthly"
                                ? "per month"
                                : "per year"
                              : "Life Time"}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </div>

                {plan?.name === "Premium" ? (
                  <button
                    onClick={() => navigate("/contact")}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-400 text-white py-2 rounded-xl font-semibold shadow hover:from-blue-700 hover:to-blue-500 mb-6 transition-all duration-200 flex items-center justify-center"
                  >
                    {plan.button}
                  </button>
                ) : (
                  <div className="w-full flex justify-center gap-4">
                    <a
                      href={
                        plan?.type === "purchase"
                          ? "https://retailpos.snapposs.com/Authentication/signup"
                          : "https://shorturl.at/AAxg1"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${plan.button2 ? "w-1/2" : "w-full"} bg-gradient-to-r from-blue-600 to-blue-400 text-white py-2 rounded-xl font-semibold shadow hover:from-blue-700 hover:to-blue-500 mb-6 transition-all duration-200 flex items-center justify-center`}
                    >
                      {plan.button}
                    </a>
                    {plan.button2 && (
                      <a
                        href="https://shorturl.at/0lAAE"
                        rel="noopener noreferrer"
                        className="w-1/2 bg-gradient-to-r from-blue-600 to-blue-400 text-white py-2 rounded-xl font-semibold shadow hover:from-blue-700 hover:to-blue-500 mb-6 transition-all duration-200 flex items-center justify-center"
                      >
                        {plan.button2}
                      </a>
                    )}
                  </div>
                )}

                <ul className="text-gray-700 text-sm space-y-2 mb-2">
                  {plan.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 bg-blue-400 rounded-full"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Retail;
